import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import type {
  AssetSummaryResponse,
  ExtractionJobResponse,
  PropertyListingResponse,
} from '@/client/types.gen';
import { showErrorToast, showInfoToast, showSuccessToast } from '@/components/ui';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { activityKeys } from '@/lib/activity-api';
import { assetKeys, getAssetPage } from '@/lib/asset-api';
import { connectAuthenticatedSse } from '@/lib/authenticated-sse';
import { extractionJobKeys, listExtractionJobs } from '@/lib/extraction-jobs-api';
import { FeatureFlags } from '@/lib/feature-flag-constants';
import { getListings, listingKeys } from '@/lib/listing-api';
import { ProcessingDock } from './ProcessingDock';
import {
  assetCandidate,
  extractionCandidate,
  listingCandidate,
} from './processing-adapters';
import type { ProcessingItem, ProcessingState } from './processing-model';
import { terminalStates } from './processing-model';
import {
  useTrackedProcessingItems,
  type ProcessingCandidate,
} from './useTrackedProcessingItems';

export interface ProcessingScope {
  kind: 'home' | 'group';
  id: string;
}

interface ProcessingCenterProviderProps {
  children: ReactNode;
  scope: ProcessingScope | null;
}

interface WorkspaceEventPayload {
  job_id?: string;
  asset_id?: string;
  listing_id?: string;
}

const NO_JOBS: ExtractionJobResponse[] = [];
const NO_ASSETS: AssetSummaryResponse[] = [];
const NO_LISTINGS: PropertyListingResponse[] = [];

export function ProcessingCenterProvider({ children, scope }: ProcessingCenterProviderProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const assetsEnabled = useFeatureGate(FeatureFlags.HOME_ASSETS);
  const scopeKey = scope ? `${scope.kind}:${scope.id}` : 'none';
  const [forced, setForced] = useState<{ scopeKey: string; keys: Set<string> }>({
    scopeKey,
    keys: new Set(),
  });
  const [open, setOpen] = useState(false);
  const [retryingKey, setRetryingKey] = useState<string | null>(null);

  const homeId = scope?.kind === 'home' ? scope.id : undefined;
  const groupId = scope?.kind === 'group' ? scope.id : undefined;

  const extractionQuery = useApiQuery(
    homeId ? extractionJobKeys.all(homeId) : ['extractionJobs', 'none'],
    () => listExtractionJobs(homeId!),
    { enabled: !!homeId, staleTime: 15_000, refetchInterval: 30_000 },
  );
  const assetQuery = useApiQuery(
    assetKeys.page(homeId ?? 'none', { limit: 100 }),
    () => getAssetPage(homeId!, { limit: 100 }),
    { enabled: !!homeId && assetsEnabled, staleTime: 15_000, refetchInterval: 30_000 },
  );
  const listingQuery = useApiQuery(
    listingKeys.all(groupId ?? 'none'),
    () => getListings(groupId!),
    { enabled: !!groupId, staleTime: 15_000, refetchInterval: 30_000 },
  );

  const retry = useCallback(async (key: string, operation: () => Promise<unknown>) => {
    setRetryingKey(key);
    try {
      await operation();
    } catch {
      showErrorToast(intl.formatMessage({
        id: '4fe675b3-7c54-4597-a11f-b1e0accbe576',
        defaultMessage: 'This item could not be queued again. Please retry.',
      }));
    } finally {
      setRetryingKey(null);
    }
  }, [intl]);

  const candidates = useMemo<ProcessingCandidate[]>(() => {
    if (!scope) return [];
    const context = { intl, scopeId: scope.id, retryingKey, retry, queryClient };
    if (scope.kind === 'group') {
      return (listingQuery.data ?? NO_LISTINGS).map((listing) => listingCandidate(listing, context));
    }
    return [
      ...(extractionQuery.data ?? NO_JOBS).map((job) => extractionCandidate(job, context)),
      ...(assetQuery.data?.items ?? NO_ASSETS).map((asset) => assetCandidate(asset, context)),
    ];
  }, [assetQuery.data, extractionQuery.data, intl, listingQuery.data, queryClient, retry, retryingKey, scope]);

  const ready = scope?.kind === 'home'
    ? extractionQuery.data !== undefined
    : scope?.kind === 'group'
      ? listingQuery.data !== undefined
      : false;
  const forcedKeys = useMemo(
    () => forced.scopeKey === scopeKey ? forced.keys : new Set<string>(),
    [forced, scopeKey],
  );
  const { items, dismissAll } = useTrackedProcessingItems(scopeKey, candidates, ready, forcedKeys);

  useEffect(() => {
    setForced((current) => current.scopeKey === scopeKey
      ? current
      : { scopeKey, keys: new Set() });
    setOpen(false);
    setRetryingKey(null);
  }, [scopeKey]);

  useEffect(() => {
    if (!scope) return;
    const path = scope.kind === 'home'
      ? `/api/home/${scope.id}/events`
      : `/api/explore/groups/${scope.id}/events`;

    return connectAuthenticatedSse(path, {
      onEvent: (event) => {
        let payload: WorkspaceEventPayload = {};
        try {
          payload = JSON.parse(event.data) as WorkspaceEventPayload;
        } catch {
          // The authoritative refresh below also handles malformed payloads.
        }

        const key = eventKey(event.event, payload);
        if (key) {
          setForced((current) => {
            const keys = current.scopeKey === scopeKey ? new Set(current.keys) : new Set<string>();
            keys.add(key);
            return { scopeKey, keys };
          });
        }

        if (scope.kind === 'group') {
          void queryClient.invalidateQueries({ queryKey: listingKeys.all(scope.id) });
          if (payload.listing_id) {
            void queryClient.invalidateQueries({ queryKey: listingKeys.detail(payload.listing_id) });
          }
          return;
        }

        if (event.event === 'asset.enrichment_status_changed') {
          void queryClient.invalidateQueries({ queryKey: assetKeys.all(scope.id) });
          return;
        }
        void queryClient.invalidateQueries({ queryKey: extractionJobKeys.all(scope.id) });
        void queryClient.invalidateQueries({ queryKey: activityKeys.all });
        void queryClient.invalidateQueries({ queryKey: ['homeDocuments', scope.id] });
        void queryClient.invalidateQueries({ queryKey: ['homeExpenses', scope.id] });
        void queryClient.invalidateQueries({ queryKey: ['schedule', scope.id] });
      },
    });
  }, [queryClient, scope, scopeKey]);

  useTerminalNotifications(scopeKey, items);

  const previousKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    const keys = new Set(items.map((item) => item.key));
    if (items.some((item) => !previousKeys.current.has(item.key))) setOpen(true);
    previousKeys.current = keys;
  }, [items]);

  return (
    <>
      {children}
      {scope && items.length > 0 && (
        <ProcessingDock
          items={items}
          open={open}
          onOpenChange={setOpen}
          onDismiss={dismissAll}
        />
      )}
    </>
  );
}

function eventKey(eventType: string, payload: WorkspaceEventPayload): string | null {
  if (eventType === 'extraction.job_status_changed' && payload.job_id) return `document:${payload.job_id}`;
  if (eventType === 'listing.enrichment_status_changed' && payload.listing_id) return `listing:${payload.listing_id}`;
  if (eventType === 'asset.enrichment_status_changed' && payload.asset_id) return `asset:${payload.asset_id}`;
  return null;
}

function useTerminalNotifications(scopeKey: string, items: ProcessingItem[]): void {
  const intl = useIntl();
  const previousStates = useRef(new Map<string, ProcessingState>());
  const notified = useRef(new Set<string>());

  useEffect(() => {
    previousStates.current.clear();
    notified.current.clear();
  }, [scopeKey]);

  useEffect(() => {
    for (const item of items) {
      const previous = previousStates.current.get(item.key);
      previousStates.current.set(item.key, item.state);
      if (!terminalStates.has(item.state)) {
        for (const state of terminalStates) {
          notified.current.delete(`${scopeKey}:${item.key}:${state}`);
        }
        continue;
      }
      if (previous === item.state) continue;
      const key = `${scopeKey}:${item.key}:${item.state}`;
      if (notified.current.has(key)) continue;
      notified.current.add(key);
      queueMicrotask(() => notifyTerminal(intl, item));
    }
  }, [intl, items, scopeKey]);
}

function notifyTerminal(intl: ReturnType<typeof useIntl>, item: ProcessingItem): void {
  if (item.state === 'failed') {
    showErrorToast(intl.formatMessage(
      { id: '6633e6f3-0c34-4a24-b39b-dfcb2383cd85', defaultMessage: "We couldn't process {title}." },
      { title: item.notificationTitle },
    ));
  } else if (item.state === 'needs_action') {
    showInfoToast(intl.formatMessage(
      { id: '8d15148c-9455-4a7b-b037-d2f7d1e35486', defaultMessage: '{title} is ready for review.' },
      { title: item.notificationTitle },
    ));
  } else {
    showSuccessToast(intl.formatMessage(
      { id: '4d81ef20-a09e-4a78-95f7-1bfd603d1a3b', defaultMessage: '{title} is ready.' },
      { title: item.notificationTitle },
    ));
  }
}
